// Local-only kindness progress tracker (no auth).
// Stored in localStorage keyed by lesson id + activity index.

import { api } from "@/lib/api";

const STORAGE_KEY = "twomk_progress_v1";
const CLIENT_ID_KEY = "twomk_client_id_v1";

const POINTS_BY_DIFFICULTY = {
    easy: 5,
    medium: 10,
    "big-impact": 20,
};

function getOrCreateClientId() {
    if (typeof window === "undefined") return "anon";
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
        id = (window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
}

export const BADGES = [
    { id: "mustard-seed", points: 25, title: "Mustard Seed", verse: "Matthew 17:20", color: "bg-mint", emoji: "🌱" },
    { id: "servants-heart", points: 50, title: "Servant's Heart", verse: "Mark 10:45", color: "bg-sun", emoji: "💛" },
    { id: "light-of-the-world", points: 100, title: "Light of the World", verse: "Matthew 5:14", color: "bg-coral", emoji: "🔥" },
    { id: "second-mile-hero", points: 200, title: "2nd Mile Hero", verse: "Matthew 5:41", color: "bg-lilac", emoji: "👑" },
];

function read() {
    if (typeof window === "undefined") return { entries: {}, certificates: [] };
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { entries: {}, certificates: [] };
        const parsed = JSON.parse(raw);
        return { entries: parsed.entries || {}, certificates: parsed.certificates || [] };
    } catch {
        return { entries: {}, certificates: [] };
    }
}

function write(state) {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new Event("twomk-progress-updated"));
}

function pointsForActivity(activity) {
    return POINTS_BY_DIFFICULTY[activity?.difficulty] || 5;
}

// Get done-state for a lesson
export function getDoneFor(lessonId) {
    const state = read();
    const out = {};
    Object.keys(state.entries).forEach((key) => {
        if (key.startsWith(`${lessonId}::`)) {
            const idx = key.split("::")[1];
            out[idx] = true;
        }
    });
    return out;
}

// Toggle one activity. Returns { done, newlyEarnedBadge, totalPoints }
export function toggleActivity(lesson, activityIdx) {
    const state = read();
    const key = `${lesson.id}::${activityIdx}`;
    const activity = lesson.community_activities[activityIdx];
    const wasDone = !!state.entries[key];

    if (wasDone) {
        delete state.entries[key];
    } else {
        state.entries[key] = {
            lessonId: lesson.id,
            lessonTitle: lesson.title,
            theme: lesson.theme,
            activityIdx,
            activityTitle: activity?.title || "",
            difficulty: activity?.difficulty || "easy",
            points: pointsForActivity(activity),
            completedAt: new Date().toISOString(),
        };
        // Fire-and-forget anonymous community log (best-effort)
        try {
            const clientId = getOrCreateClientId();
            api.post("/kindness/log", {
                lesson_id: lesson.id,
                activity_idx: activityIdx,
                points: pointsForActivity(activity),
                client_id: clientId,
                theme: lesson.theme || null,
            }).catch(() => {});
        } catch { /* ignore */ }
    }

    const totalsBefore = computeTotals(read().entries);
    const totalsAfter = computeTotals(state.entries);

    // Detect badge crossing
    let newlyEarnedBadge = null;
    if (!wasDone) {
        for (const b of BADGES) {
            if (totalsBefore.points < b.points && totalsAfter.points >= b.points) {
                newlyEarnedBadge = b;
                if (!state.certificates.includes(b.id)) {
                    state.certificates.push(b.id);
                }
                break;
            }
        }
    }

    write(state);
    return { done: !wasDone, newlyEarnedBadge, totals: totalsAfter };
}

function computeTotals(entries) {
    let points = 0;
    let deeds = 0;
    const days = new Set();
    Object.values(entries).forEach((e) => {
        points += e.points || 0;
        deeds += 1;
        days.add((e.completedAt || "").slice(0, 10));
    });
    return { points, deeds, daysActive: days.size };
}

export function getProgress() {
    const state = read();
    const totals = computeTotals(state.entries);
    const earnedBadges = BADGES.filter((b) => totals.points >= b.points);
    const nextBadge = BADGES.find((b) => totals.points < b.points) || null;
    const recent = Object.values(state.entries)
        .sort((a, b) => (b.completedAt || "").localeCompare(a.completedAt || ""))
        .slice(0, 12);
    return { ...totals, earnedBadges, nextBadge, recent, certificates: state.certificates };
}

export function resetProgress() {
    write({ entries: {}, certificates: [] });
}
